import { Tooltip, Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDeviceFloppy, IconExclamationCircle } from '@tabler/icons-react';
import { api } from '~/trpc-extra/api';
import { useProjectId } from '~/hooks/context/useProjectId';
import { useTestSuiteDialog } from '~/hooks/useTestSuiteDialog';
import type { TestListItem } from '~/server/api/routers/test';
import { useMemo } from 'react';

export const TestSuiteLabel = ({ test }: { test: TestListItem }) => {
  const trpcUtils = api.useUtils();
  const projectId = useProjectId();

  const testSuites = useMemo(() => test.testSuites.sort((a, b) => a.name.localeCompare(b.name)), [test.testSuites]);

  const { mutate: updateTestMembership } = api.testSuite.updateTests.useMutation({
    onMutate: () => {
      notifications.show({
        color: 'gray',
        message: `Adding test to suite`,
        icon: <IconDeviceFloppy />,
      });
    },
    onSuccess: () => {
      notifications.show({
        color: 'teal',
        message: `Added test to suite`,
        icon: <IconDeviceFloppy />,
      });
      trpcUtils.testSuite.list.invalidate().catch(() => undefined);
      trpcUtils.testSuite.get.invalidate().catch(() => undefined);
      trpcUtils.test.listTests.invalidate().catch(() => undefined);
    },
    onError: (e) => {
      console.error(e);
      notifications.show({
        color: 'red',
        title: 'Error',
        message: `${e}`,
        icon: <IconExclamationCircle />,
      });
    },
  });

  const addToTestSuite = useTestSuiteDialog({
    onSuiteClick: (testSuiteId) => {
      updateTestMembership({
        operation: 'add',
        projectId: projectId!,
        testSuiteId,
        testIds: [test.id],
      });
    },
  });

  return (
    <div>
      {testSuites.length > 0 ?
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {testSuites.map((suite) => (
            <Tooltip
              key={suite.id}
              label={suite.name}
            >
              <Button
                variant="light"
                color="blue"
                size="compact-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`/project/${projectId}/testSuite/${suite.id}`, '_blank', 'noopener,noreferrer');
                }}
              >
                {suite.name}
              </Button>
            </Tooltip>
          ))}
        </div>
      : <Button
          onClick={(e) => {
            addToTestSuite();
            e.stopPropagation();
          }}
          className="opacity-60 transition-all hover:opacity-100"
          color="gray"
          variant="subtle"
          size="compact-xs"
        >
          + Add to suite
        </Button>
      }
    </div>
  );
};
